<template>
  <b-overlay :show="loading" :opacity="0.6">
    <b-row class="mt-3" no-gutters :class="$smallScreen ? 'flex-nowrap' : ''">
      <div v-show="!$isCustomer">
        <b-form-radio-group
          v-model="filterBy"
          class="mr-3"
          :options="filterOptions"
          buttons
          button-variant="secondary"
        />
      </div>
      <div>
        <b-form-group>
          <b-input-group>
            <b-form-input
              id="filterInput"
              v-model="filter"
              variant="position-relative"
              type="search"
              placeholder="Type to Filter"
              :debounce="500"
            ></b-form-input>
          </b-input-group>
        </b-form-group>
      </div>
      <b-pagination
        v-if="!$smallScreen"
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="pageSize"
        aria-controls="my-table"
        align="left"
        class="ml-auto w-auto"
      ></b-pagination>
    </b-row>
    <mobile-table v-if="$smallScreen" :items="unitInspections" :fields="fields">
      <template #[`name`]="{ item }">
        <safe-hyperlink :to="'/inspections/' + item.unitInspectionId">
          {{ item.name }}
        </safe-hyperlink>
      </template>
      <template #[`serviceOrderId`]="{ item }">
        <label :class="`col-head`">Service Order ID:</label>
        <safe-hyperlink
          v-if="item.serviceOrderId"
          :to="'/units/' + item.unitId + '/service-orders/' + item.serviceOrderId"
        >
          {{ item.serviceOrderId }}
        </safe-hyperlink>
      </template>
      <template #[`branchId`]="{ item }">
        <label :class="`col-head`">Branch:</label>
        <div>{{ item.branchId }} - {{ item.branchName }}</div>
      </template>
      <template #[`dateCompleted`]="{ item }">
        <label :class="`col-head`">Completed:</label>
        <div>{{ item.dateCompleted | date }}</div>
      </template>
      <template #[`completedBy`]="{ item }">
        <label :class="`col-head`">Completed By:</label>
        <employee-display v-if="item.empCompleted" :employee="item.empCompleted" :show-email="false" />
      </template>
    </mobile-table>
    <b-table
      v-else
      class="border"
      striped
      sticky-header="50vh"
      :items="unitInspections"
      :fields="fields"
      :per-page="pageSize"
      :current-page="currentPage"
      :filter="filter"
      :sort-by.sync="sortBy"
      show-empty
      empty-text="No results found."
      empty-filtered-text="No results found."
      @filtered="onFiltered"
    >
      <template #cell(name)="{ item }">
        <safe-hyperlink :to="'/inspections/' + item.unitInspectionId">
          {{ item.name }}
        </safe-hyperlink>
      </template>
      <template #cell(serviceOrderId)="{ item }">
        <safe-hyperlink
          v-show="(item.posted && $isCustomer) || !$isCustomer"
          :to="'/units/' + item.unitId + '/service-orders/' + item.serviceOrderId"
        >
          {{ item.serviceOrderId }}
        </safe-hyperlink>
      </template>
      <template #cell(branchId)="{ item }">
        <div>{{ item.branchId }} - {{ item.branchName }}</div>
      </template>
      <template #cell(dateCompleted)="{ item }">
        <div>{{ item.dateCompleted | date }}</div>
      </template>
      <template #cell(completedBy)="{ item }">
        <employee-display v-if="item.empCompleted" :employee="item.empCompleted" :show-email="false" />
      </template>

      <template #cell(actions)="{ item }">
        <div class="text-center">
          <b-dropdown
            size="xsm"
            right
            variant="secondary"
            no-caret
            boundary="viewport"
            :disabled="item.serviceOrderId != null"
          >
            <template #button-content>
              <font-awesome-icon icon="ellipsis-h" />
            </template>
            <b-dropdown-item v-if="!$isCustomer" @click="openServiceOrderAside(item)">
              Assign Service Order
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>
    <transition v-if="!$smallScreen" name="slide">
      <unit-service-order-history-aside
        v-if="showServiceOrderAside"
        v-model="serviceOrderWithJob"
        class="fixed-right fixed-top"
        header="Assign Service Order"
        :subheader="selectedInspection.name"
        :loading="asideIsBusy"
        selectable
        jobs-only
        :on-close="attemptCloseAside"
      >
        <div class="p-2 float-right">
          <b-button variant="primary" :disabled="assigning" @click="attemptAssignInspection">
            <div v-show="assigning">
              <b-spinner small></b-spinner>
              Assign...
            </div>
            <div v-show="!assigning">Assign</div>
          </b-button>
        </div>
      </unit-service-order-history-aside>
    </transition>
    <unsaved-changes-modal
      v-if="!$smallScreen"
      ref="UnsavedChangesModal"
      message="You have unsaved changes. What would you like to do?"
      :continue-btn-text="`Continue With Assignment`"
      :has-changes="isServiceOrderSelected"
    ></unsaved-changes-modal>
    <unsaved-changes-modal
      v-if="!$smallScreen"
      ref="ConfirmAssignmentModal"
      title="Assign to Service Order"
      :continue-btn-text="`Assign`"
    >
      <p>
        Are you sure you want to assign
        <b>{{ selectedInspection.name }}</b>
        to SO ID#
        <router-link :to="'/units/' + selectedInspection.unitId + '/service-orders/' + selectedServiceOrderId">
          <span>{{ selectedServiceOrderId }}</span>
        </router-link>
        <span>?</span>
      </p>
    </unsaved-changes-modal>
  </b-overlay>
</template>

<script>
// components
import SafeHyperlinkComponent from '@/shared/components/ui/SafeHyperlinkComponent';
import EmployeeDisplayComponent from '@/shared/components/ui/EmployeeDisplayComponent';
// vuex
import { UnitActions, UnitGetters } from '@/shared/store/unit/types';
import { InspectionActions } from '@/shared/store/inspection/types';
import { mapActions } from 'vuex';
// helpers
import ErrorService from '@/shared/services/ErrorService';
import SuccessService from '@/shared/services/SuccessService';

export default {
  name: 'UnitInspections',
  components: {
    'safe-hyperlink': SafeHyperlinkComponent,
    'employee-display': EmployeeDisplayComponent,
    'unsaved-changes-modal': () => import('@/shared/components/UnsavedChangesModal'),
    'mobile-table': () => import('@/shared/components/MobileTableComponent'),
    'unit-service-order-history-aside': () => import('@/shared/components/service-order/UnitServiceOrderHistoryAside')
  },
  beforeRouteLeave(to, from, next) {
    if (this.isServiceOrderSelected()) {
      const closeThenNext = function (cont) {
        this.closeAside(cont);
        next(cont);
      };
      this.$refs.UnsavedChangesModal.show(this, closeThenNext.bind(this));
    } else {
      next();
    }
  },
  data: function () {
    return {
      pageSize: 50,
      currentPage: 1,
      totalRows: 0,
      filter: null,
      sortBy: null,
      loading: false,
      asideIsBusy: false,
      assigning: false,
      serviceOrderWithJob: null,
      selectedInspection: {},
      cachedInspections: null,
      filterBy: this.$router.currentRoute.query.filterBy ? this.$router.currentRoute.query.filterBy : null,
      filterOptions: [
        { text: 'All', value: null },
        { text: 'Open', value: 'Open' },
        { text: 'Completed', value: 'Completed' }
      ]
    };
  },
  computed: {
    unitInspections() {
      const unitInspections = this.$store.getters[UnitGetters.GET_UNIT_INSPECTIONS];
      if (this.filterBy === 'Open') {
        return unitInspections.filter(i => i.dateCompleted == null);
      } else if (this.filterBy === 'Completed') {
        return unitInspections.filter(i => i.dateCompleted != null || i.Posted == true);
      } else {
        if (this.$isCustomer) unitInspections.filter(i => i.dateCompleted != null || i.Posted == true);
        return unitInspections;
      }
    },
    fields() {
      const fields = [];
      fields.push({ key: 'name', label: 'Inspection', sortable: true });
      fields.push({ key: 'branchId', label: 'Branch', sortable: true });
      fields.push({ key: 'serviceOrderId', label: 'Service Order ID', sortable: true, collapsible: true });
      fields.push({ key: 'dateCompleted', label: 'Completed', sortable: true, collapsible: true });
      fields.push({ key: 'completedBy', label: 'Completed By', sortable: true, collapsible: true });
      fields.push({ key: 'failedItems', label: 'Items Failed', sortable: true });
      if (!this.$smallScreen && !this.$isCustomer) fields.push({ key: 'actions', label: 'Actions' });
      return fields;
    },
    showServiceOrderAside() {
      return this.$store.getters[UnitGetters.GET_UNIT_SERVICE_ORDERS_ASIDE_SHOW];
    },
    selectedServiceOrderId() {
      return this.serviceOrderWithJob?.serviceOrder?.serviceOrderId;
    }
  },
  watch: {
    filterBy(value) {
      let query = null;
      if (value != null) {
        query = { filterBy: value };
      }
      this.$router.push({ query }).catch(() => {});
    }
  },
  created() {
    this.fetchUnitInspections();
  },
  methods: {
    ...mapActions([
      InspectionActions.ASSIGN_SERVICE_ORDER,
      UnitActions.FETCH_UNIT_INSPECTIONS,
      UnitActions.SHOW_SERVICE_ORDER_HISTORY_ASIDE,
      UnitActions.HIDE_SERVICE_ORDER_HISTORY_ASIDE,
      UnitActions.FETCH_UNIT,
      UnitActions.FETCH_UNIT_SERVICE_ORDER_HISTORY
    ]),
    async fetchUnitInspections() {
      try {
        this.loading = true;
        await this[UnitActions.FETCH_UNIT_INSPECTIONS](this.$route.params.unitId);
      } catch (error) {
        if (error.response && error.response.status == 404) {
          this.notFound = true;
        }
      } finally {
        this.loading = false;
      }
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    async openServiceOrderAside(inspection) {
      this[UnitActions.SHOW_SERVICE_ORDER_HISTORY_ASIDE]();
      this.selectedInspection = inspection;
      await this.loadServiceOrderData(inspection.unitId);
    },
    attemptAssignInspection() {
      if (!this.serviceOrderWithJob) {
        ErrorService.createErrorToast(this, 'Please select an operation to assign.');
        return;
      }
      this.$refs.ConfirmAssignmentModal.show(this, null, this.assignInspection);
    },
    async assignInspection() {
      try {
        this.assigning = true;
        const unitInspectionId = this.selectedInspection.unitInspectionId;
        const serviceOrderId = this.serviceOrderWithJob.serviceOrder.serviceOrderId;
        const operationId = this.serviceOrderWithJob.selectedJob.operationId;
        await this[InspectionActions.ASSIGN_SERVICE_ORDER]({
          unitInspectionId,
          inspection: { serviceOrderId, operationId }
        });
        await this[UnitActions.FETCH_UNIT_INSPECTIONS](this.$route.params.unitId);
        this.totalRows = this.unitInspections.length;
        this.closeAside();
        SuccessService.createSuccessToast(
          this.$root,
          `Assigned Service Order ID: ${serviceOrderId} to ${this.selectedInspection.name} successfully.`
        );
      } catch {
        ErrorService.createErrorToast(this, 'Error assigning service order to inspection.');
      } finally {
        this.assigning = false;
      }
    },
    isServiceOrderSelected() {
      return this.serviceOrderWithJob != null;
    },
    async attemptCloseAside() {
      if (this.isServiceOrderSelected()) {
        this.$refs.UnsavedChangesModal.show(this, this.closeAside);
      } else {
        await this.closeAside();
      }
    },
    async closeAside(cont = true) {
      if (cont) {
        await this[UnitActions.HIDE_SERVICE_ORDER_HISTORY_ASIDE]();
        this.serviceOrderWithJob = null;
        this.selectedInspection = {};
      }
    },
    async loadServiceOrderData(unitId) {
      this.asideIsBusy = true;
      try {
        await this[UnitActions.FETCH_UNIT](unitId);
        await this[UnitActions.FETCH_UNIT_SERVICE_ORDER_HISTORY]({
          unitId,
          params: { jobsOnly: true, status: 'Open' }
        });
      } catch (error) {
        ErrorService.createErrorToast(this, 'Error fetching Service Order history.');
      } finally {
        this.asideIsBusy = false;
      }
    }
  }
};
</script>
